import { useState, useEffect } from 'react';
import { AlertCircle, Download, RefreshCw, ExternalLink } from 'lucide-react';

interface PluginInfo {
  version: string;
  download_url: string;
  changelog: string;
  last_updated: string;
}

interface PluginUpdateNotificationProps {
  currentVersion?: string;
}

export function PluginUpdateNotification({ currentVersion = '6.4.0' }: PluginUpdateNotificationProps) {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [pluginInfo, setPluginInfo] = useState<PluginInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    checkForUpdates();
  }, []);

  const checkForUpdates = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/plugin-update-info`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch plugin info');
      }

      const data = await response.json();
      setLatestVersion(data.version);
      setPluginInfo(data);
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const compareVersions = (v1: string, v2: string): number => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    return 0;
  };

  const isUpdateAvailable = latestVersion && compareVersions(latestVersion, currentVersion) > 0;

  if (loading) {
    return null;
  }

  if (!isUpdateAvailable) {
    return null;
  }

  const formatChangelog = (changelog: string) => {
    const versionSection = changelog.split('<h4>')[1];
    if (!versionSection) return changelog;

    const content = versionSection.split('</h4>')[1]?.split('<h4>')[0] || '';
    return content
      .replace(/<ul>/g, '')
      .replace(/<\/ul>/g, '')
      .replace(/<li>/g, '• ')
      .replace(/<\/li>/g, '\n')
      .replace(/<strong>/g, '')
      .replace(/<\/strong>/g, '')
      .trim();
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6 shadow-lg">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
            <Download className="w-6 h-6 text-white" />
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">
              Plugin Update Available!
            </h3>
          </div>

          <p className="text-gray-700 mb-4">
            Version <span className="font-bold text-blue-600">{latestVersion}</span> is now available.
            You're currently running version <span className="font-semibold">{currentVersion}</span>.
          </p>

          <div className="bg-white rounded-lg p-4 mb-4 border border-blue-100">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-600" />
              How to Update (2 minutes):
            </h4>
            <ol className="space-y-2 text-sm text-gray-700 ml-6">
              <li className="list-decimal">
                <strong>Go to your WordPress Admin Dashboard</strong>
              </li>
              <li className="list-decimal">
                Navigate to <strong>Plugins → Installed Plugins</strong>
              </li>
              <li className="list-decimal">
                Click <strong>"Check for Updates"</strong> at the top of the page
              </li>
              <li className="list-decimal">
                Find "Resume Doctor AI" and click <strong>"Update Now"</strong>
              </li>
            </ol>
          </div>

          {showDetails && pluginInfo && (
            <div className="bg-white rounded-lg p-4 mb-4 border border-blue-100">
              <h4 className="font-semibold text-gray-900 mb-3">What's New in {latestVersion}:</h4>
              <div className="text-sm text-gray-700 whitespace-pre-line">
                {formatChangelog(pluginInfo.sections?.changelog || '')}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
            >
              {showDetails ? 'Hide' : 'Show'} What's New
            </button>

            <a
              href={pluginInfo?.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-white text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm flex items-center gap-2"
            >
              Download Manually
              <ExternalLink className="w-4 h-4" />
            </a>

            <button
              onClick={checkForUpdates}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Recheck
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Updates are automatically detected by WordPress every 12 hours. Manual check speeds up the process.
          </p>
        </div>
      </div>
    </div>
  );
}
